// apps/academy/src/components/AcademySidebar.tsx
// Academy K: Refonte — enrolled + default courses only, CTA catalogue, icônes accès
import { DIFFICULTY_LABELS } from '@neuroforge/shared'
import { useAuthStore } from '@neuroforge/shared/client'
import {
  cn,
  WaveSVG,
  AppIcon,
  IconHome,
  IconLockOpen,
  IconDiamond,
  IconLock,
  IconChartBar,
  IconShield,
  IconBooks,
  IconChevronDown,
  Logo,
  AppSwitcher,
} from '@neuroforge/ui'
import { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation, useParams } from 'react-router'

import { useCourseNav } from '@/hooks/useCourses'

/** Icône d'accès leçon (Academy K: 3 niveaux) */
function LessonAccessIcon({ isFree, isPremium }: { isFree?: boolean; isPremium?: boolean }) {
  if (isFree)
    return (
      <span title="Gratuit" className="shrink-0 text-green-500">
        <AppIcon icon={IconLockOpen} size={16} aria-label="Gratuit" />
      </span>
    )
  if (isPremium)
    return (
      <span title="Premium" className="text-(--accent) shrink-0">
        <AppIcon icon={IconDiamond} size={16} aria-label="Premium" />
      </span>
    )
  return (
    <span title="Connexion requise" className="text-(--muted) shrink-0">
      <AppIcon icon={IconLock} size={16} aria-label="Connexion requise" />
    </span>
  )
}

export default function AcademySidebar() {
  const location = useLocation()
  const params = useParams()
  const { isAuthenticated, user } = useAuthStore()

  // Academy K: getNavData retourne uniquement les cours enrolled + default
  const { data: navData, isLoading } = useCourseNav()
  const courses = useMemo(() => navData?.courses ?? [], [navData])
  const globalProgress = navData?.globalProgress ?? 0
  const totalLessons = navData?.totalLessons ?? 0
  const completedLessons = navData?.completedLessons ?? 0

  const _activeCourseSlug = params.slug ?? params.courseSlug
  const activeLessonSlug = params.lessonSlug

  // Modules repliables — fermés par défaut, on auto-ouvre celui de la leçon active.
  const [openIds, setOpenIds] = useState<Set<string>>(new Set())
  const activeCourseId = useMemo(
    () =>
      courses.find((c) => c.modules.some((m) => m.lessons.some((l) => l.slug === activeLessonSlug)))
        ?.id,
    [courses, activeLessonSlug],
  )
  useEffect(() => {
    if (!activeCourseId) return
    setOpenIds((prev) => (prev.has(activeCourseId) ? prev : new Set(prev).add(activeCourseId)))
  }, [activeCourseId])

  const toggleCourse = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="flex h-full flex-col">
      {/* ─── Switcher global entre les apps NeuroForge (haut de sidebar) ─── */}
      {/* z-50 : passe au-dessus des sections `relative` de la sidebar. Pas
          d'overflow-hidden sur ce conteneur, sinon le dropdown serait rogné. */}
      <div className="border-(--border) relative z-50 border-b px-3 py-2">
        <AppSwitcher current="academy" />
      </div>

      {/* ─── Hero card (branding + metrics) — template sidebar hero ─── */}
      <div className="border-(--border) border-b p-3">
        <div className="relative overflow-hidden rounded-[28px] px-2 pt-2">
          <div className="relative z-10">
            <div className="mb-4 flex items-center gap-3">
              <NavLink to="/" className="flex shrink-0 items-center justify-center">
                <Logo size={88} />
              </NavLink>
              <div>
                <p className="text-(--accent) text-[10px] font-extrabold uppercase tracking-[0.24em]">
                  NeuroForge
                </p>
                <h1 className="text-xl font-extrabold tracking-tight">Academy</h1>
              </div>
            </div>
          </div>
          <p className="text-(--muted) mb-4 text-sm leading-relaxed">
            Plateforme d'apprentissage et d'auto-formation IA gratuite
          </p>
          {/* Wave SVG overlay */}
          <div className="leading-0 pointer-events-none absolute inset-x-0 bottom-0">
            <WaveSVG className="h-12.5 w-full opacity-60" />
          </div>
        </div>
      </div>

      {/* ─── Navigation + Modules tree (scrollable) ─── */}
      <nav className="flex-1 space-y-5 overflow-y-auto p-5">
        {/* Nav links */}
        <section className="">
          <NavLink
            to="/"
            className={cn(
              'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
              location.pathname === '/'
                ? 'text-(--accent) bg-(--accent-subtle) font-semibold'
                : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
            )}
          >
            <AppIcon icon={IconBooks} size={20} aria-hidden /> Explorer le catalogue
          </NavLink>

          <NavLink
            to="/mon-parcours"
            className={cn(
              'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
              location.pathname === '/mon-parcours'
                ? 'text-(--accent) bg-(--accent-subtle) font-semibold'
                : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
            )}
          >
            <AppIcon icon={IconHome} size={20} aria-hidden /> Mon Parcours
          </NavLink>
          {isAuthenticated && (
            <>
              <NavLink
                to="/progress"
                className={cn(
                  'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
                  location.pathname === '/progress'
                    ? 'text-(--accent) bg-(--accent-subtle) font-semibold'
                    : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
                )}
              >
                <AppIcon icon={IconChartBar} size={20} aria-hidden /> Ma Progression
              </NavLink>
              {user?.role === 'ADMIN' && (
                <NavLink
                  to="/admin"
                  className={cn(
                    'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
                    location.pathname.startsWith('/admin')
                      ? 'bg-red-500/10 font-semibold text-red-500'
                      : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
                  )}
                >
                  <AppIcon icon={IconShield} size={20} aria-hidden /> Admin
                </NavLink>
              )}
            </>
          )}
        </section>

        {/* ─── Academy K: Enrolled + default courses only ─── */}
        {!isLoading &&
          courses.map((course) => {
            const courseProgress =
              course.totalLessons > 0
                ? Math.round((course.completedLessons / course.totalLessons) * 100)
                : 0
            const diffLabel = DIFFICULTY_LABELS[course.difficulty as keyof typeof DIFFICULTY_LABELS]
            const accentColor = diffLabel?.color ?? '#6366f1'
            const accentGradient = `linear-gradient(90deg, ${accentColor}, #06b6d4)`
            const isOpen = openIds.has(course.id)

            return (
              <section
                key={course.id}
                className="glass relative overflow-hidden rounded-[28px] p-4"
              >
                {/* Top accent bar — like template .module-card::before */}
                <div
                  className="absolute inset-x-0 top-0 h-1"
                  style={{ background: accentGradient }}
                />

                <div
                  className={cn('mt-1 flex items-start justify-between gap-3', isOpen && 'mb-4')}
                >
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <p
                        className="text-[10px] font-black uppercase tracking-[0.18em]"
                        style={{ color: accentColor }}
                      >
                        Module
                      </p>
                      {course.isDefault && (
                        <span className="bg-[var(--accent)]/10 text-(--accent) rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">
                          Inclus
                        </span>
                      )}
                    </div>
                    <NavLink to={`/courses/${course.slug}`}>
                      <h3 className="hover:text-(--accent) text-base font-extrabold tracking-tight transition-colors">
                        {course.title}
                      </h3>
                    </NavLink>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="rounded-full px-2.5 py-1 text-xs font-bold"
                      style={Object.assign(
                        {},
                        {
                          background: `${accentColor}15`,
                          color: accentColor,
                        },
                      )}
                    >
                      {courseProgress}%
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleCourse(course.id)}
                      aria-expanded={isOpen}
                      aria-label={isOpen ? 'Replier le module' : 'Déplier le module'}
                      className="text-(--muted) hover:text-(--text) hover:bg-(--bg-elevated) flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors"
                    >
                      <AppIcon
                        icon={IconChevronDown}
                        size={20}
                        className={cn('transition-transform duration-200', isOpen && 'rotate-180')}
                        aria-hidden
                      />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <>
                    {/* Progress bar */}
                    <div className="mb-4 h-2 overflow-hidden rounded-full bg-[var(--border)]">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={Object.assign(
                          {},
                          {
                            width: `${courseProgress}%`,
                            background: accentGradient,
                          },
                        )}
                      />
                    </div>

                    {/* Lessons list with access icons (Academy K) */}
                    <div className="space-y-1">
                      {course.modules.flatMap((mod) =>
                        mod.lessons.map((lesson) => {
                          const isActive = activeLessonSlug === lesson.slug
                          return (
                            <NavLink
                              key={lesson.id}
                              to={`/courses/${course.slug}/lessons/${lesson.slug}`}
                              className={cn(
                                'flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-semibold transition-all',
                                'text-(--text-secondary)',
                                isActive &&
                                  'from-[var(--accent)]/12 bg-linear-to-r text-(--accent) border-l-2 border-[var(--accent)] to-transparent',
                                !isActive && 'hover:bg-(--bg-elevated) hover:translate-x-0.5',
                              )}
                            >
                              {/* Academy K: icône d'accès ou dot complétée */}
                              {lesson.isCompleted ? (
                                <div
                                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                                  style={Object.assign(
                                    {},
                                    {
                                      background: accentColor,
                                      boxShadow: `0 0 12px ${accentColor}`,
                                    },
                                  )}
                                />
                              ) : (
                                <LessonAccessIcon
                                  isFree={lesson.isFree}
                                  isPremium={lesson.isPremium}
                                />
                              )}
                              <span className="truncate">{lesson.title}</span>
                            </NavLink>
                          )
                        }),
                      )}
                    </div>
                  </>
                )}
              </section>
            )
          })}
      </nav>

      {/* ─── Academy K: CTA Catalogue ─── */}
      <div className="px-5 pb-2">
        <NavLink
          to="/"
          className={cn(
            'glass flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold transition-all',
            location.pathname === '/'
              ? 'text-(--accent) bg-(--accent-subtle) border-[var(--accent)]'
              : 'text-(--accent) hover:bg-(--accent-subtle)',
          )}
        >
          <AppIcon icon={IconBooks} size={20} aria-hidden /> Découvrir d'autres modules
        </NavLink>
      </div>

      {/* ─── Progression globale (bas de sidebar) — template footer card ─── */}
      <div className="border-(--border) border-t p-5">
        <div className="glass rounded-3xl p-4">
          <div className="mb-2 flex items-center justify-between text-[10px] font-black uppercase tracking-[0.18em] opacity-70">
            <span>Progression globale</span>
            <span>{globalProgress}%</span>
          </div>
          <div className="mb-4 h-2 overflow-hidden rounded-full bg-[var(--border)]">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={Object.assign(
                {},
                {
                  width: `${globalProgress}%`,
                  background: 'linear-gradient(90deg, #6366f1, #22d3ee)',
                },
              )}
            />
          </div>
          <div className="text-(--muted) grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="mb-1 font-bold uppercase tracking-[0.18em]">Leçons</div>
              <div className="text-(--text) text-sm font-semibold">{totalLessons}</div>
            </div>
            <div>
              <div className="mb-1 font-bold uppercase tracking-[0.18em]">Terminées</div>
              <div className="text-(--text) text-sm font-semibold">{completedLessons}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
